import React, {
  createContext,
  PropsWithChildren,
  Reducer,
  useContext,
  useEffect,
  useReducer,
} from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import { discoPrototypeStore } from 'client/app/apps/disco-prototype/dataStore/dexieStore';
import { stateFromStore } from 'client/app/apps/disco-prototype/state/fromStore';
import { LiquidSet } from 'client/app/apps/disco-prototype/state/liquidSet';
import reducer, {
  Action,
  INITIAL_STATE,
} from 'client/app/apps/disco-prototype/state/reducer';
import { State } from 'client/app/apps/disco-prototype/state/state';
import { Step } from 'client/app/apps/disco-prototype/state/steps';
import { Table } from 'client/app/apps/disco-prototype/state/table';

type StateContextType = {
  tables: Table[];
  liquidSets: LiquidSet[];
  steps: Step[];
  handleUpdateResponse: (tables: Table[], liquidSets: LiquidSet[], steps: Step[]) => void;
  handleAddStep: () => void;
  handleDeleteStep: (id: string) => void;
  handleUpdateStep: (id: string, update: Record<string, any>) => void;
};

export const StateContext = createContext<StateContextType | undefined>(undefined);

export const useStateContext = () => {
  const context = useContext(StateContext);
  if (context === undefined) {
    throw new Error('usePlateContext must be used within a PlateProvider');
  }
  return context;
};

export function StateProvider({ children }: PropsWithChildren) {
  const datasets = useLiveQuery(() => discoPrototypeStore.getDatasets(), []);
  const liquidsets = useLiveQuery(() => discoPrototypeStore.getLiquidsets(), []);
  const plates = useLiveQuery(() => discoPrototypeStore.getPlates(), []);

  const [state, dispatch] = useReducer<Reducer<State, Action>>(reducer, INITIAL_STATE);

  useEffect(() => {
    if (datasets && liquidsets && plates) {
      const { tables, liquidSets } = stateFromStore(datasets, liquidsets, plates);
      dispatch({ type: 'initialize', payload: { tables, liquidSets } });
      dispatch({ type: 'createStep' });
    }
  }, [datasets, liquidsets, plates]);

  const handleAddStep = () => dispatch({ type: 'createStep' });
  const handleDeleteStep = (id: string) => dispatch({ type: 'deleteStep', toDelete: id });
  const handleUpdateStep = (id: string, update: Record<string, any>) =>
    dispatch({ type: 'updateStep', toUpdate: id, update: { type: 'update', ...update } });
  const handleUpdateResponse = (
    tables: Table[],
    liquidSets: LiquidSet[],
    steps: Step[],
  ) => dispatch({ type: 'updateResponse', tables, liquidSets, steps });

  const value = {
    ...state,
    handleUpdateResponse,
    handleDeleteStep,
    handleUpdateStep,
    handleAddStep,
  };

  return <StateContext.Provider value={value}>{children}</StateContext.Provider>;
}
